// The two kinds of file a site holds, and the submenu the archived project put
// under "Files" — Images and Documents.
//
// A kind is not a label. It selects the bucketws BUCKET the bytes go to, and
// each bucket carries its own extension allowlist and its own size ceiling, so
// "images" and "documents" differ in what they will accept rather than only in
// which tab they appear under. That is what the bucket's `exts`, `minFileSize`
// and `maxFileSize` fields are for, and it is why this is two buckets per
// organization and not one bucket with a tag.
//
// It is also part of the partition key — see `siteFileKey` — so each tab reads
// exactly one DynamoDB partition and a listing needs no filter at all.
import { PUBLIC, PRIVATE } from "./visibility";

export const IMAGES = "images";
export const DOCUMENTS = "documents";

export const KINDS = [IMAGES, DOCUMENTS];

export const isKind = (value) => KINDS.includes(value);

// What each kind's bucket accepts, and the ceiling it accepts it to.
//
// The extensions are lowercase and bare — no dot — because that is the shape
// bucketws stores and validates (`^[a-z0-9]{1,10}$`) and the shape the app
// derives from a filename.
//
// The image list is deliberately the set bucketws can also generate minimaps
// for, plus svg. svg is included because a site needs logos and icons, and
// excluded from any expectation of a generated size: it is not a raster and
// bucketws' minimap handler does not touch it.
//
// The ceilings are per-file and are enforced by S3 itself — bucketws builds a
// `content-length-range` condition into the presigned POST — so a browser that
// ignores them gets a rejection from S3 rather than an oversized object.
export const KIND_CONFIG = {
  [IMAGES]: {
    label: "Images",
    exts: ["jpg", "jpeg", "png", "gif", "webp", "svg", "avif", "ico"],
    minFileSize: 1,
    maxFileSize: 10 * 1024 * 1024,
    // See `defaultVisibilityOf` below. This is the value a NEW row gets; it is
    // not consulted for a row that already has one.
    visibility: PUBLIC,
  },
  [DOCUMENTS]: {
    label: "Documents",
    exts: [
      "pdf", "txt", "md", "csv", "json", "xml",
      "doc", "docx", "xls", "xlsx", "ppt", "pptx",
      "zip", "woff", "woff2", "ttf", "otf",
    ],
    minFileSize: 1,
    maxFileSize: 50 * 1024 * 1024,
    visibility: PRIVATE,
  },
};

// WHAT A NEWLY UPLOADED FILE OF EACH KIND IS FOR, EXPRESSED AS A DEFAULT
//
// Images public, documents private, and the asymmetry is the point rather than
// a hedge. An image in a CMS is put there to appear on a page: defaulting it
// private would mean every logo and every screenshot works in the editor and
// breaks the moment it is published, diagnosed by nobody, because the byte
// route answers a miss identically to a refusal (it must — telling them apart
// would say which files a site holds).
//
// A document is not that. "Files" is also where the price list and the contract
// go, and a `.pdf` that becomes world-readable because it was uploaded is a
// disclosure nobody asked for. So the kind whose failure is a broken image
// defaults open and the kind whose failure is a leak defaults closed, and
// either can be changed on the file's own screen.
//
// This is consulted ONLY at create. @common/schema/site-file/visibility says
// why a row that predates the field is private whatever its kind.
export const defaultVisibilityOf = (kind) =>
  KIND_CONFIG[kind]?.visibility || PRIVATE;

// The bucket-level quota requested at provisioning, in bytes. bucketws turns
// this into a datum limit value at create, and P156 in docs/pending.md is the
// standing defect that a bucket older than its limit has no threshold row —
// which is why this is set at create and never relied upon to appear later.
//
// AND IT CANNOT EXCEED 2^31-1, WHICH IS WHY IT IS NOT A ROUND POWER OF TWO
//
// `BucketInputCreate.size` is a GraphQL `Int`, and GraphQL's Int is 32-bit
// SIGNED — 2,147,483,647. The obvious value here was 5 GiB, and it failed on
// the first real provisioning call with
//
//   Int cannot represent non 32-bit signed integer value: 5368709120
//
// which surfaces as a 500 from bucketws and an upload that cannot start. Note
// where that check happens: in bucketws' variable coercion, before any resolver
// runs. Nothing on this side could have caught it — the value is valid JSON, a
// valid JS number and a valid quota; it is only invalid as a wire type, so it
// takes a real call to the real schema to find. `kind.test.js` pins the ceiling
// so the next person raising this does not rediscover it in a Lambda log.
//
// 2 GB decimal rather than 2 GiB (2,147,483,648) — one byte over the ceiling is
// the same failure, and a value chosen for a limit should not sit on it.
export const INT_MAX = 2147483647;
export const KIND_BUCKET_SIZE = 2_000_000_000;

// WHICH FILES THE DETAILS SCREEN RENDERS INLINE
//
// The images kind's own list minus `svg`, and the exclusion is not a judgement
// about vector graphics. bucketws stores every "active" content type — html,
// xml, pdf and svg among them — as `application/octet-stream` with
// `Content-Disposition: attachment`, because user content is served from a
// domain that holds a session cookie and an `.svg` can carry a `<script>`
// (`@repoflow/common/file-utils`, `safeContentHeaders`). That is the right
// storage decision and it is not this service's to undo, but it means an
// `<img src>` pointed at one gets an attachment and renders a broken image.
// So svg is offered as a download, like every document.
//
// Derived from KIND_CONFIG rather than written out again, so a new image
// extension is previewable the moment it is accepted.
export const PREVIEW_EXTS = KIND_CONFIG[IMAGES].exts.filter((ext) => ext !== "svg");

export const isPreviewExt = (ext) => PREVIEW_EXTS.includes(ext);
